import { POLONIA_CREST as logoUrl } from './clubIdentity';
import {
  Activity,
  Dumbbell,
  BrainCircuit,
  LayoutDashboard,
  Shield,
  Swords,
  CalendarCheck,
  Users,
  Waypoints,
  Trophy,
  Settings,
  Zap,
  Radio,
  Newspaper,
  ClipboardList,
  Search,
  X,
} from 'lucide-react';
import { useI18n } from './I18nContext';
import { useData } from './DataContext';
import { resolveBundledClub } from './leagueClubs';
import { useMemo, useState } from 'react';
import { moduleAllowedForRole, normalizeMembershipRole, roleLabel } from './staffAccess';

export type ModuleId =
  | 'dashboard'
  | 'players'
  | 'matches'
  | 'matchday'
  | 'analysis'
  | 'training'
  | 'intelligence'
  | 'lineup'
  | 'harmony'
  | 'league'
  | 'news'
  | 'settings'
  | 'operations';

type Props = {
  active: ModuleId;
  onNavigate: (module: ModuleId) => void;
  onLogout: () => void;
  children: React.ReactNode;
};

export function AppShell({ active, onNavigate, onLogout, children }: Props) {
  const { language, setLanguage, t } = useI18n();
  const { team, teams, players, setActiveTeam } = useData();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const activeClub = resolveBundledClub(
    team?.name || team?.club_name || 'Polonia Glasgow'
  );
  const membershipRole = normalizeMembershipRole(team?.role);

  const items = [
    ['dashboard', t.dashboard, LayoutDashboard],
    ['operations', 'Operations', ClipboardList],
    ['players', t.players, Users],
    ['matches', t.matches, Swords],
    ['matchday', t.matchday, CalendarCheck],
    ['analysis', t.analysis, Activity],
    ['training', t.training, Dumbbell],
    ['intelligence', t.intelligence, BrainCircuit],
    ['lineup', t.lineup, Shield],
    ['harmony', t.harmony, Waypoints],
    ['league', t.league, Trophy],
    ['news', t.news, Newspaper],
    ['settings', t.settings, Settings],
  ] as const;

  const visibleItems = items.filter(([id]) => moduleAllowedForRole(id, membershipRole));
  const commandResults = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return [];
    const moduleMatches = visibleItems
      .filter(([id,label]) => String(id).includes(q) || String(label).toLowerCase().includes(q))
      .map(([id,label]) => ({kind:'module' as const,id,label:String(label)}));
    const playerMatches = players.filter(p=>p.name.toLowerCase().includes(q) || String(p.squad_number ?? '').includes(q))
      .slice(0,8).map(p=>({kind:'player' as const,id:'players' as ModuleId,label:p.name}));
    return [...moduleMatches,...playerMatches].slice(0,12);
  }, [searchText, visibleItems, players]);

  return (
    <div
      className="app-shell"
      style={{
        '--active-club-primary': activeClub?.primaryColor || '#0756c9',
        '--active-club-secondary': activeClub?.secondaryColor || '#f4c542',
      } as React.CSSProperties}
    >
      <header className="command-header">
        <div className="brand-block">
          <button
            className="crest-frame home-crest-button"
            type="button"
            aria-label={t.backToDashboard}
            title={t.backToDashboard}
            onClick={() => onNavigate('dashboard')}
          >
            <img src={logoUrl} alt="Polonia Glasgow AFC" />
            <span className="home-crest-indicator">⌂</span>
          </button>
          <button className="brand-copy-button" type="button" onClick={() => onNavigate('dashboard')}>
            <div className="brand-title">{t.appName}</div>
            <div className="brand-subtitle">{t.subtitle}</div>
          </button>
        </div>
        <div className="header-actions">
          <button className="header-search-button" type="button" onClick={()=>setSearchOpen(true)} aria-label="Search">
            <Search size={18}/><span>Search</span>
          </button>
          <span className={`header-role-badge ${membershipRole}`}>
            {roleLabel(membershipRole)}
          </span>
          {teams.length > 1 && (
            <select
              className="team-select"
              aria-label={t.teamSelection}
              value={team?.id ?? ''}
              onChange={(event) => void setActiveTeam(event.target.value)}
            >
              {teams.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.name || candidate.club_name || 'Team'}
                </option>
              ))}
            </select>
          )}
          <select value={language} onChange={(e) => setLanguage(e.target.value as 'en' | 'pl')}>
            <option value="en">EN</option>
            <option value="pl">PL</option>
          </select>
          <button className="steel-button" onClick={onLogout}>{t.logout}</button>
        </div>
      </header>

      <div className="command-layout">
        <aside className="desktop-nav">
          {visibleItems.map(([id, label, Icon]) => (
            <button
              key={id}
              className={active === id ? 'nav-item active' : 'nav-item'}
              onClick={() => onNavigate(id)}
            >
              <Icon size={20} />
              <span>{label}</span>
            </button>
          ))}
        </aside>
        <main className="content-stage">{children}</main>
      </div>

      {membershipRole !== 'player' && <div className="matchday-quick-dock" aria-label={t.matchdayQuickAccess}>
        <button type="button" onClick={() => onNavigate('lineup')} title={t.lineup}>
          <Shield size={17} /><span>{t.lineup}</span>
        </button>
        <button type="button" className="primary" onClick={() => onNavigate('matchday')} title={t.matchday}>
          <Zap size={18} /><span>{t.matchday}</span>
        </button>
        <button
          type="button"
          onClick={() => {
            try { localStorage.setItem('ecc_match_centre_requested_view', 'live'); } catch {}
            onNavigate('matches');
          }}
          title={t.liveMatch}
        >
          <Radio size={17} /><span>{t.liveMatch}</span>
        </button>
      </div>}

      {searchOpen && <div className="command-palette-backdrop" onClick={()=>setSearchOpen(false)}>
        <section className="command-palette" onClick={e=>e.stopPropagation()}>
          <div className="command-palette-input"><Search size={20}/><input autoFocus placeholder="Search player, module or action…" value={searchText} onChange={e=>setSearchText(e.target.value)}/><button onClick={()=>setSearchOpen(false)}><X size={20}/></button></div>
          <div className="command-palette-results">
            {commandResults.map((result,index)=><button key={`${result.kind}-${result.label}-${index}`} onClick={()=>{onNavigate(result.id as ModuleId);setSearchOpen(false);setSearchText('')}}><span>{result.kind==='player'?'PLAYER':'MODULE'}</span><strong>{result.label}</strong></button>)}
            {searchText && !commandResults.length && <p>No match found.</p>}
          </div>
        </section>
      </div>}

      <nav className="mobile-nav">
        {visibleItems.map(([id, label, Icon]) => (
          <button
            key={id}
            type="button"
            className={active === id ? 'nav-item active' : 'nav-item'}
            aria-current={active === id ? 'page' : undefined}
            aria-label={label}
            onClick={() => onNavigate(id)}
          >
            <Icon size={19} />
            <span>{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
